"use client";

import { JSX, useEffect, useRef, useState } from "react";
import Footer from "rivals/components/app/Layout/Footer";
import Body from "rivals/components/pages/Article/Body";
import ErrorBoundary from "rivals/components/pages/Article/ErrorBoundary";
import InfiniteAd from "rivals/components/shared/Ads/Benji/InfiniteAd";
import PublisherAd from "rivals/components/shared/Ads/Publisher";
import MediaWrapper from "rivals/components/shared/Media";
import Spinner from "rivals/components/shared/Spinner";
import { MEDIA_BREAKPOINTS } from "rivals/media";
import useFetchContent from "rivals/services/fetchContent/useFetchContent";
import {
  isPubAdProps,
  PageComponent
} from "rivals/shared/interfaces/PageConfig";
import { setData } from "rivals/shared/utils/qa";
import styles from "./InfiniteScroll.module.scss";
import type { Props } from "./types";

export default function InfiniteScroll({
  giftingEnabled,
  site,
  index,
  infiniteScrollMeta,
  publisherAds = [],
  slug
}: Props): JSX.Element {
  const observerTarget = useRef<HTMLDivElement>(null);
  const [pubAd1, pubAd2] = publisherAds;

  const [shouldFetchNextArticle, setShouldFetchNextArticle] = useState(false);

  const [prevScrollTop, setPrevScrollTop] = useState<number>();
  const [showNextArticle, setShowNextArticle] = useState<boolean>(false);

  const nextArticleId = infiniteScrollMeta.infiniteFeed[index]?.contentId;

  const { data: nextArticle } = useFetchContent(nextArticleId, {
    shouldFetch: shouldFetchNextArticle && !!nextArticleId
  });

  const publisherAd = (adComponent: PageComponent): JSX.Element | null => {
    return adComponent &&
      isPubAdProps(adComponent.props) &&
      adComponent.props.href &&
      adComponent.props.imageUrl ? (
      <MediaWrapper lessThan={MEDIA_BREAKPOINTS.MD}>
        <ErrorBoundary>
          <PublisherAd
            href={adComponent.props.href}
            imageUrl={adComponent.props.imageUrl}
          />
        </ErrorBoundary>
      </MediaWrapper>
    ) : null;
  };

  useEffect(() => {
    if (nextArticle) {
      setPrevScrollTop(document.documentElement.scrollTop);
      setShowNextArticle(true);
    }
  }, [nextArticle]);

  useEffect(() => {
    if (showNextArticle && prevScrollTop) {
      document.documentElement.scrollTop = prevScrollTop;
      setPrevScrollTop(undefined);
    }
  }, [showNextArticle, prevScrollTop]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        const target = entries[0];
        if (target.isIntersecting && nextArticleId) {
          setShouldFetchNextArticle(true);
          if (observerTarget.current) {
            observer.unobserve(observerTarget.current);
          }
        }
      },
      {
        // TODO: https://griosf.atlassian.net/browse/RVLS-9770
        // Increase bottom margin to trigger fetch earlier (or see intersection observer options in MDN)
        rootMargin: "0px 0px 200px 0px"
      }
    );

    const element = observerTarget.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [nextArticleId, observerTarget, site.contentSpaceId]);

  return (
    <div
      className={styles.container}
      {...setData("article", "infinite-scroll")}
    >
      <div ref={observerTarget} />
      {nextArticleId && (
        <Footer areArticleLinks giftingEnabled={giftingEnabled} />
      )}
      {shouldFetchNextArticle && !showNextArticle && (
        <Spinner className={styles.loadingSpinner} />
      )}
      {nextArticle && showNextArticle && (
        <>
          <div>
            {/* show publisher ads only after first article (before first infinite article) */}
            {index === 0 && publisherAd(pubAd1)}
            <InfiniteAd slotType="ldrb" />
            {index === 0 && publisherAd(pubAd2)}
          </div>
          <Body
            article={nextArticle}
            giftingEnabled={giftingEnabled}
            index={index + 1}
            infiniteScrollMeta={infiniteScrollMeta}
            site={site}
            slug={slug}
          />
        </>
      )}
    </div>
  );
}
